// src/services/azureProjectAssignmentService.js
// Azure Table Storage Service für Projekt-Mitarbeiter:in-Zuordnungen

const AZURE_CONFIG = {
  storageAccount: "mfprojects", // Dein Storage Account Name
  tableName: "projectEmployeeAssignments",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D", // Ersetze mit deinem SAS Token
};

// Hilfsfunktion für RowKey
const createRowKey = (projectNumber, employeeUuid) => {
  return `${projectNumber}_${employeeUuid}`;
};

// Alle Zuordnungen für ein Projekt laden
export const getProjectAssignments = async (projectIdentifier) => {
  try {
    let allAssignments = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    const filter = `$filter=ProjectNumber eq '${projectIdentifier}'`;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&${filter}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allAssignments = allAssignments.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    const assignments = allAssignments.map((item) => ({
      id: item.RowKey,
      projectNumber: item.ProjectNumber,
      employeeUuid: item.EmployeeUuid,
      productUuid: item.ProductUuid,
      role: item.Role || "",
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));

    return assignments;
  } catch (error) {
    console.error("Assignment Load Error:", error);
    return [];
  }
};

// Standard-Leistung für eine Mitarbeiter:in in einem Projekt laden
export const getEmployeeDefaultProduct = async (
  projectNumber,
  employeeUuid
) => {
  try {
    const rowKey = `${projectNumber}_${employeeUuid}`;
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      return null;
    }

    const data = await response.json();

    // Rückgabe der ProductUuid (mit grossem U!)
    return data.ProductUuid;
  } catch (error) {
    return null;
  }
};

// Zuordnungen für ein Projekt speichern/aktualisieren
export const saveProjectAssignments = async (
  projectNumber,
  projectId,
  assignments
) => {
  if (!assignments && Array.isArray(projectId)) {
    assignments = projectId;
    projectId = null;
  }

  try {
    await deleteAllProjectAssignments(projectNumber);

    const savePromises = assignments.map((assignment) =>
      saveAssignment(
        projectNumber,
        projectId,
        assignment.employeeUuid,
        assignment.productUuid,
        assignment.role || ""
      )
    );

    await Promise.all(savePromises);

    return true;
  } catch (error) {
    throw error;
  }
};

// Einzelne Zuordnung speichern
const saveAssignment = async (
  projectNumber,
  projectId,
  employeeUuid,
  productUuid,
  role = ""
) => {
  const rowKey = createRowKey(projectNumber, employeeUuid);
  const now = new Date().toISOString();

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const entity = {
    PartitionKey: "project",
    RowKey: rowKey,
    ProjectNumber: projectNumber,
    ProjectId: projectId || projectNumber, // Fallback
    EmployeeUuid: employeeUuid,
    ProductUuid: productUuid,
    Role: role || "",
    CreatedAt: now,
    UpdatedAt: now,
  };

  const response = await fetch(url, {
    method: "PUT", // PUT für Upsert (Insert or Replace)
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json;odata=nometadata",
    },
    body: JSON.stringify(entity),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure Save Error: ${response.status}`);
  }

  return true;
};

// Alle Zuordnungen für ein Projekt löschen
export const deleteAllProjectAssignments = async (projectNumber) => {
  try {
    // 1. Alle bestehenden Zuordnungen laden
    const existingAssignments = await getProjectAssignments(projectNumber);

    // 2. Jede Zuordnung löschen
    const deletePromises = existingAssignments.map((assignment) =>
      deleteAssignment(assignment.projectNumber, assignment.employeeUuid)
    );

    await Promise.all(deletePromises);

    return true;
  } catch (error) {
    throw error;
  }
};

// Einzelne Zuordnung löschen
const deleteAssignment = async (projectNumber, employeeUuid) => {
  const rowKey = createRowKey(projectNumber, employeeUuid);
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='project',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "If-Match": "*", // Löscht ohne Etag-Prüfung
    },
  });

  if (!response.ok && response.status !== 404) {
    const errorText = await response.text();
    throw new Error(`Azure Delete Error: ${response.status}`);
  }

  return true;
};

// Alle Projekte mit ihren Zuordnungen laden (für Übersicht)
export const getAllProjectsWithAssignments = async () => {
  try {
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status}`);
    }

    const data = await response.json();

    // Gruppiere nach code
    const groupedByProject = {};

    data.value.forEach((item) => {
      const projectNumber = item.ProjectNumber;

      if (!groupedByProject[projectNumber]) {
        groupedByProject[projectNumber] = {
          projectNumber,
          assignmentCount: 0,
          assignments: [],
        };
      }

      groupedByProject[projectNumber].assignments.push({
        employeeUuid: item.EmployeeUuid,
        productUuid: item.productUuid,
      });
      groupedByProject[projectNumber].assignmentCount++;
    });

    return Object.values(groupedByProject);
  } catch (error) {
    return [];
  }
};

// Einzelne Person einem Projekt zuordnen (ohne bestehende Zuordnungen zu löschen).
// Existiert bereits eine Zuordnung (z.B. mit Leistung aus dem Controlling), bleibt
// sie unverändert – so gehen Controlling-Daten nicht verloren.
export const addProjectMember = async (
  projectNumber,
  employeeUuid,
  projectId = null
) => {
  const existing = await getProjectAssignments(projectNumber);
  if (existing.some((a) => a.employeeUuid === employeeUuid)) return true;
  return saveAssignment(projectNumber, projectId, employeeUuid, "", "");
};

// Einzelne Zuordnung entfernen (wirkt auch im Controlling – gemeinsame Tabelle).
export const removeProjectMember = async (projectNumber, employeeUuid) => {
  return deleteAssignment(projectNumber, employeeUuid);
};

// Konfiguration aktualisieren (für andere SAS Token etc.)
export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
